const LOCALE_BY_LANGUAGE = {
    init: 'es-ES',
    es: 'es-ES',
    en: 'en-US',
    fr: 'fr-FR',
    de: 'de-DE',
    pt: 'pt-PT',
    it: 'it-IT',
    pl: 'pl-PL',
    cat: 'ca-ES'
}

let getRegionalLocales = () => []

/** Presentation only: keep legacy unit identifiers and canonical readings unchanged. */
export function normalizeTemperatureUnit(unit = '°C') {
    return /^(?:[°º]\s*)?f(?:ahrenheit)?$/i.test(String(unit).trim()) ? '°F' : '°C'
}

export function temperatureUnitSuffix(unit, language) {
    return (/^en(?:-|$)/i.test(localeForLanguage(language)) ? '' : '\u00a0') + normalizeTemperatureUnit(unit)
}

export function formatTemperature(value, language, unit = '°C', { maximumFractionDigits = 2, emptyValue = '-' } = {}) {
    if (!['number', 'string'].includes(typeof value) || (typeof value === 'string' && !value.trim())) return emptyValue
    let numeric = Number(value)
    if (!Number.isFinite(numeric)) return emptyValue
    if (Math.abs(numeric) < 0.5 * Math.pow(10, -maximumFractionDigits)) numeric = 0
    const number = new Intl.NumberFormat(localeForLanguage(language), { useGrouping: false, maximumFractionDigits }).format(numeric)
    return number + temperatureUnitSuffix(unit, language)
}

/** Normalize explicit temperature text in label previews; never change barcode data. */
export function formatTemperatureLabel(value, language) {
    if (typeof value !== 'string') return value
    return value.replace(/([+-]?\d+(?:[.,]\d+)?)\s*(?:([-–])\s*([+-]?\d+(?:[.,]\d+)?))?\s*[°º]\s*([CF])\b/gi, (_, first, separator, last, unit) => {
        const start = Number(first.replace(',', '.'))
        if (last === undefined) return formatTemperature(start, language, unit)
        const number = new Intl.NumberFormat(localeForLanguage(language), { useGrouping: false, maximumFractionDigits: 2 }).format(start)
        return number + ' - ' + formatTemperature(Number(last.replace(',', '.')), language, unit)
    })
}

/** Present weights from explicit source units; quantities stored by the domain stay untouched. */
export function formatWeight(value, language, sourceUnit = 'g') {
    if (!['number', 'string'].includes(typeof value) || (typeof value === 'string' && !value.trim())) return '-'
    const numeric = Number(value)
    if (!Number.isFinite(numeric) || !/^(g|gr|kg)$/i.test(sourceUnit)) return '-'
    const grams = sourceUnit.toLowerCase() === 'kg' ? numeric * 1000 : numeric
    const kilograms = Math.abs(grams) >= 1000
    return formatNumber(kilograms ? grams / 1000 : grams, language) + '\u00a0' + (kilograms ? 'kg' : 'g')
}

/** Count names come from runtime dictionaries, not hardcoded language tables. */
export function formatUnitCount(value, language, unitLabel) {
    if (!['number', 'string'].includes(typeof value) || (typeof value === 'string' && !value.trim())) return '-'
    const numeric = Number(value)
    if (!Number.isFinite(numeric) || typeof unitLabel !== 'function') return '-'
    return formatNumber(numeric, language) + '\u00a0' + unitLabel(Math.abs(numeric) === 1 ? 1 : 2)
}

/** Localize explicit legacy measurement tokens in display names and package labels only. */
export function formatMeasurementLabel(value, language, unitLabel) {
    if (typeof value !== 'string') return value
    return value.replace(/(^|[^\w.,À-ÿ])([+-]?\d+(?:[.,]\d+)?)\s*(kg|gr|g|uds?\.?|units?|unidades?|unités?|unità|Stk\.?|Stück|un\.?|u\.?)(?![\wÀ-ÿ])/gi, (match, prefix, amount, unit) => {
        const numeric = Number(amount.replace(',', '.'))
        if (/^(g|gr|kg)$/i.test(unit)) return prefix + formatWeight(numeric, language, unit)
        return typeof unitLabel === 'function' ? prefix + formatUnitCount(numeric, language, unitLabel) : match
    })
}

/** Values are percentage points (57 means 57%); precision is presentation only. */
export function formatPercent(value, language, level = 1, { allowNegative = false, emptyValue = '-' } = {}) {
    if (!['number', 'string'].includes(typeof value) || (typeof value === 'string' && !value.trim())) return emptyValue
    let numeric = Number(value)
    if (!Number.isFinite(numeric) || ![1, 2, 3].includes(level)) return emptyValue
    const digits = level - 1
    if (!allowNegative) numeric = Math.max(0, numeric)
    if (Math.abs(numeric) < 0.5 * Math.pow(10, -digits)) numeric = 0
    return new Intl.NumberFormat(localeForLanguage(language), {
        style: 'percent', minimumFractionDigits: digits, maximumFractionDigits: digits
    }).format(numeric / 100)
}

/** Remove redundant unit annotations from titles whose values include the unit. */
export function percentLabel(value) {
    return String(value).replace(/\s*\(\s*%\s*\)/g, '').trim()
}

export function configureNumberFormatters({ regionalLocalesGetter }) {
    getRegionalLocales = regionalLocalesGetter
}

/**
 * BCP 47 locale for the active app language.
 *
 * @param {string} language
 * @returns {string}
 */
export function localeForLanguage(language) {
    const normalized = String(language || 'es').replace(/_/g, '-')
    const mapped = LOCALE_BY_LANGUAGE[normalized] || normalized
    const base = mapped.split('-')[0].toLowerCase()
    const regional = getRegionalLocales().filter(value => typeof value === 'string')
        .map(value => value.replace(/_/g, '-'))
        .find(value => value.includes('-') && value.split('-')[0].toLowerCase() === base)
    const locale = normalized.includes('-') ? normalized : (regional || mapped)
    try {
        return new Intl.NumberFormat(locale).resolvedOptions().locale
    } catch (_) {
        return LOCALE_BY_LANGUAGE.es
    }
}

/**
 * Format a measurement with the decimal separator of the active language.
 *
 * @param {number|string|null|undefined} value
 * @param {string} language
 * @param {number} [fractionDigits] Fixed fraction digits. Omit to keep up to 2.
 * @returns {string} Empty string when the value is not numeric.
 */
export function formatDecimal(value, language, fractionDigits) {
    if (value === null || value === undefined || value === '') {
        return ''
    }

    const numeric = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(numeric)) {
        return ''
    }

    const options = {}
    if (typeof fractionDigits === 'number') {
        options.minimumFractionDigits = fractionDigits
        options.maximumFractionDigits = fractionDigits
    } else {
        options.maximumFractionDigits = 2
    }

    return new Intl.NumberFormat(localeForLanguage(language), options).format(numeric)
}

/** Format display values without changing their precision or API representation. */
export function formatNumber(value, language, fractionDigits) {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') return ''
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) return ''
    const options = typeof fractionDigits === 'number'
        ? { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }
        : { maximumFractionDigits: 20 }
    return new Intl.NumberFormat(localeForLanguage(language), options).format(numeric)
}

/** Localize numeric labels (including quantities with units), preserving placeholders. */
export function formatNumberLabel(value, language, fractionDigits) {
    if (typeof value === 'number') return formatNumber(value, language, fractionDigits)
    if (typeof value !== 'string') return value
    const match = value.match(/^([+-]?\d+(?:\.\d+)?)(\s*[^\d.,]*)$/)
    const digits = fractionDigits === undefined && match && match[1].includes('.') ? match[1].split('.')[1].length : fractionDigits
    return match ? formatNumber(match[1], language, digits) + match[2] : value
}

/** Canonical number to editable localized text, without grouping. */
export function formatNumberInput(value, language) {
    if (value === null || value === undefined || value === '') return ''
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) return String(value)
    return new Intl.NumberFormat(localeForLanguage(language), { useGrouping: false, maximumFractionDigits: 20 }).format(numeric)
}

/** Parse user input strictly; never silently truncate an invalid value with parseFloat. */
export function parseNumberInput(value, language) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    if (typeof value !== 'string' || !value.trim()) return null
    const parts = new Intl.NumberFormat(localeForLanguage(language)).formatToParts(12345.6)
    const decimal = parts.find(part => part.type === 'decimal').value
    const groupPart = parts.find(part => part.type === 'group')
    let normalized = value.trim()
    if (groupPart && normalized.includes(groupPart.value)) {
        const chunks = normalized.split(decimal)
        if (chunks.length > 2 || (chunks[1] && chunks[1].includes(groupPart.value))) return null
        const groups = chunks[0].replace(/^[+-]/, '').split(groupPart.value)
        if (!/^\d{1,3}$/.test(groups[0]) || groups.slice(1).some(group => !/^\d{3}$/.test(group))) return null
        normalized = normalized.split(groupPart.value).join('')
    }
    normalized = normalized.replace(decimal, '.')
    if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)) return null
    // A foreign decimal separator must not be interpreted as the local separator.
    if (decimal !== '.' && value.includes('.') && (!groupPart || groupPart.value !== '.')) return null
    const numeric = Number(normalized)
    return Number.isFinite(numeric) ? numeric : null
}

/** Keep signs, trailing zeroes and an unfinished decimal separator while editing. */
export function decimalSeparator(language) {
    return new Intl.NumberFormat(localeForLanguage(language)).formatToParts(1.1).find(part => part.type === 'decimal').value
}

export function localizeNumberDraft(value, language) {
    return String(value == null ? '' : value).replace('.', decimalSeparator(language))
}
